import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Bookmark, Package, Wrench, Building2, Users, Briefcase, FolderKanban, ReceiptText, Loader2, type LucideIcon } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toggleSaved, setSaved, ensureSavedLoaded, useSavedMutationPending } from "../lib/saved";
import { PullToRefresh } from "../components/PullToRefresh";
import { profileKey } from "../lib/profileSession";
import { refreshFamily, useRefreshOwner } from "../lib/useRefreshOwner";
import { useI18n } from "../lib/i18n";
import { isNativeApp } from "../lib/platform";
import { MobileSavedItems, type SavedItemReference } from "../components/MobileSavedItems";

const KIND_META: Record<string, { Icon: LucideIcon; key: string }> = {
  item: { Icon: Package, key: "saved.kind.item" }, service: { Icon: Wrench, key: "saved.kind.service" },
  company: { Icon: Building2, key: "saved.kind.company" }, person: { Icon: Users, key: "saved.kind.jobseeker" }, profile: { Icon: Users, key: "saved.kind.profile" },
  job: { Icon: Briefcase, key: "saved.kind.job" }, project: { Icon: FolderKanban, key: "saved.kind.project" }, rfq: { Icon: ReceiptText, key: "saved.kind.rfq" },
};

export default function Saved() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [items, setItems] = useState<SavedItemReference[] | null>(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [mobileBusyKey, setMobileBusyKey] = useState<string | null>(null);
  const [removed, setRemoved] = useState<{ item: SavedItemReference; index: number } | null>(null);

  const savedPending = useSavedMutationPending();
  const identity = `${profileKey(user)}:saved`;
  const refresh = useRefreshOwner(identity, signal => [refreshFamily(() => api.savedList(signal), setItems, () => { setItems([]); setRemoved(null); })], { enabled: !!user });
  const loadItems = () => refresh.reload();
  const readError = mobileError || refresh.error;
  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  async function remove(it: SavedItemReference) {
    refresh.cancel();
    setItems((prev) => (prev ? prev.filter((x) => !(x.kind === it.kind && x.refId === it.refId)) : prev));
    await toggleSaved(it.kind, it.refId);
  }

  // [marketplace.saved-references](../../../docs/product/marketplace.md#marketplace-saved-references): only the member's saved reference changes; failed mobile removal keeps the row.
  async function removeMobile(item: SavedItemReference) {
    if (mobileBusyKey !== null || items === null) return;
    refresh.cancel();
    setMobileBusyKey(`${item.kind}:${item.refId}`); setMobileError(null);
    try {
      await ensureSavedLoaded();
      await setSaved(item.kind, item.refId, false);
      setRemoved(item.kind === "person" ? null : { item, index: items.findIndex((entry) => entry.kind === item.kind && entry.refId === item.refId) });
      setItems((current) => current?.filter((entry) => !(entry.kind === item.kind && entry.refId === item.refId)) ?? null);
    } catch { setMobileError(t("saved.mobile.removeError")); }
    finally { setMobileBusyKey(null); }
  }

  async function undoMobile() {
    if (!removed || mobileBusyKey !== null) return;
    refresh.cancel();
    const { item, index } = removed;
    setMobileBusyKey(`${item.kind}:${item.refId}`); setMobileError(null);
    try {
      await setSaved(item.kind, item.refId, true);
      setItems((current) => {
        if (!current || current.some((entry) => entry.kind === item.kind && entry.refId === item.refId)) return current;
        const restored = [...current]; restored.splice(index, 0, item); return restored;
      });
      setRemoved(null);
    } catch { setMobileError(t("saved.mobile.undoError")); }
    finally { setMobileBusyKey(null); }
  }

  return <PullToRefresh identity={`${identity}:${mobileBusyKey ?? "idle"}:${savedPending}`} disabled={savedPending || mobileBusyKey !== null || (refresh.reading && !items)} onRefresh={refresh.onRefresh}>
    <MobileSavedItems items={items} error={readError} busyKey={mobileBusyKey} canUndo={removed !== null}
      onRemove={removeMobile} onUndo={undoMobile} onRetry={loadItems} />
    <div className="container-x hidden py-6 max-w-3xl sm:block">
      <div className="flex items-center gap-2 mb-1"><Bookmark className="w-6 h-6 text-brand-red" /><h1 className="text-2xl font-extrabold text-navy">{t("saved.title")}</h1></div>
      <p className="text-mut text-sm mb-5">{t("saved.description")}</p>
      {readError && <p role="alert" className="mb-3 text-sm text-brand-red">{readError}</p>}
      {readError && !items ? (
        <div className="card p-10 text-center text-sm text-mut">{!isNativeApp() && <button type="button" onClick={loadItems} className="btn-outline mt-3">{t("saved.mobile.retry")}</button>}</div>
      ) : !items ? (
        <div className="py-16 text-center text-mut"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center">
          <Bookmark className="w-8 h-8 text-mut mx-auto mb-2" />
          <div className="font-bold text-navy">{t("saved.empty.title")}</div>
          <p className="text-sm text-mut mt-1">{t("saved.empty.description")}</p>
          <Link href="/marketplace" className="btn-navy mt-4 inline-flex">{t("saved.browse")}</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const meta = KIND_META[it.kind] || { Icon: Bookmark, key: "" };
            const Icon = meta.Icon;
            return (
              <div key={`${it.kind}:${it.refId}`} className="card p-3 flex items-center justify-between gap-3">
                <Link href={it.link} className="flex items-center gap-3 min-w-0 hover:text-brand-red">
                  <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{it.title}</div>
                      <div className="text-xs text-mut truncate"><span className="chip bg-navy-50 text-navy-600 mr-1">{meta.key ? t(meta.key) : it.kind}</span>{it.subtitle}</div>
                  </div>
                </Link>
                <button onClick={() => remove(it)} className="btn-ghost h-9 px-3 text-xs text-brand-red shrink-0"><Bookmark className="w-4 h-4 fill-brand-red" />{t("saved.remove")}</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </PullToRefresh>;
}
